import { memo, useMemo, useState } from 'react';
import useSWR from 'swr';
import { AlertTriangleIcon, GavelIcon, LockIcon, SearchCheckIcon, ShieldAlertIcon, ShieldXIcon, SigmaIcon, Undo2Icon } from 'lucide-react';
import PageCalloutRow, { PageCalloutProps } from '@/components/PageCalloutRow';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useBackendApi } from '@/hooks/fetch';
import { sanctionKinds } from '@/lib/sanctionCategories';
import type { SanctionStatsApiResp, SanctionStatsApiSuccessResp } from '@shared/otherTypes';
import SanctionsTimelineCard from './SanctionsTimelineCard';
import SanctionsBreakdownCards from './SanctionsBreakdownCards';


const PageCalloutRowMemo = memo(PageCalloutRow);

//Same windows the backend accepts
const ALL_TIME = 'all';
const windowOptions = [
    { value: '7', label: '7 Days' },
    { value: '14', label: '14 Days' },
    { value: '30', label: '30 Days' },
    { value: '90', label: '90 Days' },
    { value: ALL_TIME, label: 'All Time' },
];
const defaultWindow = '14';


/**
 * The sanction stats page - warns, admin jails, bans and anticheat bans over time
 */
export default function SanctionStatsPage() {
    const [windowDays, setWindowDays] = useState(defaultWindow);

    const sanctionStatsApi = useBackendApi<SanctionStatsApiResp>({
        method: 'GET',
        path: `/sanctionStatsData`,
    });
    const swrDataApiResp = useSWR(`/sanctionStatsData?days=${windowDays}`, async () => {
        const data = await sanctionStatsApi({ queryParams: { days: windowDays } });
        if (!data) throw new Error('empty_response');
        if ('fail_reason' in data) throw new Error(data.fail_reason);
        return data as SanctionStatsApiSuccessResp;
    }, {
        revalidateOnFocus: false,
    });

    const respData = swrDataApiResp.data;
    const isError = !!swrDataApiResp.error;
    const isLoading = !respData || swrDataApiResp.isValidating;

    const calloutRowData = useMemo(() => {
        const totals = isLoading ? undefined : respData?.totals;
        const totalSanctions = totals
            ? sanctionKinds.reduce((acc, kind) => acc + totals[kind], 0)
            : false;
        return [
            {
                label: 'Warns',
                value: totals ? totals.warn : false,
                icon: <AlertTriangleIcon />,
            },
            {
                label: 'Analyse Warns',
                value: totals ? totals.analyse : false,
                icon: <SearchCheckIcon />,
            },
            {
                label: 'Admin Jails',
                value: totals ? totals.jail : false,
                icon: <LockIcon />,
            },
            {
                label: 'AC Jails',
                value: totals ? totals.anticheatJail : false,
                icon: <ShieldAlertIcon />,
            },
            {
                label: 'Bans',
                value: totals ? totals.ban : false,
                icon: <GavelIcon />,
            },
            {
                label: 'AC Bans',
                value: totals ? totals.anticheat : false,
                icon: <ShieldAlertIcon />,
            },
            {
                label: 'Total',
                value: totalSanctions,
                icon: <SigmaIcon />,
            },
            {
                label: 'Revoked',
                value: isLoading ? false : (respData?.revoked ?? false),
                icon: <Undo2Icon />,
            },
        ] satisfies PageCalloutProps[];
    }, [respData, isLoading]);

    return (
        <div className="w-full space-y-4 md:space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-2 px-2 md:px-0">
                <div className="flex items-center gap-2 text-muted-foreground">
                    <ShieldXIcon className="size-4" />
                    <span className="text-sm">
                        {windowDays === ALL_TIME
                            ? 'Every sanction ever issued'
                            : `Sanctions issued in the last ${windowDays} days`}
                    </span>
                </div>
                <Tabs value={windowDays} onValueChange={setWindowDays}>
                    <TabsList>
                        {windowOptions.map((option) => (
                            <TabsTrigger key={option.value} value={option.value}>
                                {option.label}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                </Tabs>
            </div>

            <PageCalloutRowMemo callouts={calloutRowData} />

            <SanctionsTimelineCard
                isError={isError}
                series={isLoading ? undefined : respData?.series}
                bucketDays={isLoading ? undefined : respData?.bucketDays}
            />

            <SanctionsBreakdownCards
                isError={isError}
                totals={isLoading ? undefined : respData?.totals}
                byAdmin={isLoading ? undefined : respData?.byAdmin}
                byJob={isLoading ? undefined : respData?.byJob}
                topReasons={isLoading ? undefined : respData?.topReasons}
            />
        </div>
    );
}
